import logger from "../../../art-logger-config";
import {
  getFromLocalStorage,
  writeToLocalStorage,
} from "../../../util/localStorageUtils";
import {
  DASHBOARD_COLUMN_DEFINITIONS,
  DEFAULT_VISIBLE_COLUMNS,
} from "./columnDefinitions";

export const VALIDATION_DASHBOARD_TABLE_PREFERENCES =
  "VALIDATION_DASHBOARD_TABLE_PREFERENCES" as const;

const DEFAULT_PAGE_SIZE = 30;

export interface ValidationDashboardTablePreferences {
  readonly pageSize: number;
  readonly visibleContent: readonly string[];
}

export const DEFAULT_PREFERENCES: ValidationDashboardTablePreferences = {
  pageSize: DEFAULT_PAGE_SIZE,
  visibleContent: DEFAULT_VISIBLE_COLUMNS,
};

function getKnownColumnIds(): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const def of DASHBOARD_COLUMN_DEFINITIONS) {
    if (typeof def.id === "string") {
      ids.add(def.id);
    }
  }
  return ids;
}

function normalizePreferences(
  candidate: unknown
): ValidationDashboardTablePreferences {
  if (typeof candidate !== "object" || candidate === null) {
    return DEFAULT_PREFERENCES;
  }
  const obj = candidate as Record<string, unknown>;

  const pageSize =
    typeof obj.pageSize === "number" &&
    Number.isFinite(obj.pageSize) &&
    obj.pageSize > 0
      ? obj.pageSize
      : DEFAULT_PREFERENCES.pageSize;

  const knownIds = getKnownColumnIds();
  const visibleContent = Array.isArray(obj.visibleContent)
    ? obj.visibleContent.filter(
        (id): id is string => typeof id === "string" && knownIds.has(id)
      )
    : [];

  if (visibleContent.length === 0) {
    return { pageSize, visibleContent: DEFAULT_PREFERENCES.visibleContent };
  }

  return { pageSize, visibleContent };
}

export function readInitialPreferences(): ValidationDashboardTablePreferences {
  const raw = getFromLocalStorage(VALIDATION_DASHBOARD_TABLE_PREFERENCES);
  if (raw === null) {
    return DEFAULT_PREFERENCES;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return normalizePreferences(parsed);
  } catch (e: unknown) {
    logger.error(
      `Failed to parse ${VALIDATION_DASHBOARD_TABLE_PREFERENCES} from localStorage: ${String(
        e
      )}`
    );
    return DEFAULT_PREFERENCES;
  }
}

export function writePreferences(
  next: ValidationDashboardTablePreferences
): void {
  writeToLocalStorage(
    VALIDATION_DASHBOARD_TABLE_PREFERENCES,
    JSON.stringify(next)
  );
}
